<template>
    <button
        type="button"
        :class="['save-for-later-button', { saved: isSaved }]"
        :aria-label="`Save ${product?.productName || ''} for later`"
        @click.stop.prevent="toggleSaveForLater"
    >
        <Icon
            v-if="!isSaved"
            icon="heart"
        />
        <span
            v-else
            class="filled"
        >
            <Icon
                class="red"
                icon="heart-filled"
            />
            <Icon
                class="animation"
                icon="heart-filled"
            />
        </span>
    </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import {
    type SaveabledProduct,
    type SaveForLaterPageType,
    useSaveProductButton,
} from '@/components/saved-products/useSavedProducts'
import { useSegment } from '@/third-party/segment.ts'
import { getWindow } from '@/helpers/globals.ts'

import Icon from '@/components/ui/icon/Icon.vue'

const { product, pageType, belowCta } = defineProps<{
    product: SaveabledProduct | undefined
    pageType: SaveForLaterPageType
    belowCta?: boolean
}>()

const { addProduct, removeProduct, getIsProductSaved } = useSaveProductButton()

const isSaved = computed(() => product && getIsProductSaved(product))

const { track } = useSegment()

const toggleSaveForLater = () => {
    if (!product) return

    let eventName = 'save_product'

    if (isSaved.value) {
        eventName = 'remove_saved_product'
        removeProduct(product)
    } else {
        addProduct(product)
    }

    const url = getWindow()?.location.href

    if (!url) {
        return
    }

    const eventSuffix = pageType
    const belowCtaSuffix = belowCta ? '_below_cta' : ''

    track(`custom_${eventName}_${eventSuffix}${belowCtaSuffix}`, {
        param1: 'Save for later',
        param2: eventName,
        param3: `${eventSuffix} icon click${belowCtaSuffix.replace('_', ' ')}`,
        param4: url,
        url,
        eventName,
    })
}
</script>

<style lang="scss" scoped>
.save-for-later-button {
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    cursor: pointer;
    font-size: 20px;
    text-align: center;
    line-height: 40px;
    border-radius: 3px;
    border: none;
    position: relative;

    @include hover() {
        font-size: 22px;
    }

    &.saved {
        font-size: 22px;
    }

    .red {
        color: $red;
    }

    .filled {
        .animation {
            font-size: 20px;
            color: $red;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            animation: heartAnimation 0.4s ease-out forwards;
        }
    }
}

@keyframes heartAnimation {
    0% {
        transform: scale(1);
        opacity: 1;
    }

    100% {
        transform: scale(1.8);
        opacity: 0;
        top: 2px;
    }
}
</style>
